import React, { useRef, useEffect, useState } from 'react';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

/**
 * GlbRoom component for loading and rendering custom GLB room models
 * 
 * This component handles:
 * 1. Loading GLB files
 * 2. Extracting canvas button positions from named objects
 * 3. Setting up collision meshes
 * 4. Applying materials and lighting
 */
const GlbRoom = ({ 
    glbPath, 
    onCanvasButtonsDetected,
    onLoad,
    scale = 1,
}) => {
    const groupRef = useRef();
    const [canvasButtons, setCanvasButtons] = useState([]);
    const [isLoaded, setIsLoaded] = useState(false);
    
    // Load the GLB file
    const { scene, nodes, materials } = useGLTF(glbPath);
    
    // Process the GLB file on load
    useEffect(() => {
        if (!scene) return;
        
        console.log('GLB Room loaded:', glbPath);
        console.log('Scene nodes:', nodes);
        
        // Clone the scene to avoid modifying the cached original
        const clonedScene = scene.clone();
        
        // Extract canvas button positions from named objects
        const extractedButtons = [];
        
        clonedScene.traverse((object) => {
            // Look for objects with names starting with "canvas_button_"
            if (object.name && object.name.startsWith('canvas_button_')) {
                console.log('Found canvas button:', object.name, object.position);
                
                // Get wall ID from user data if available
                const wallId = object.userData?.wall_id || 'unknown';
                
                // Create a canvas button definition
                // Flip the rotation by adding PI (180 degrees) to the Y rotation to face the opposite direction
                // This fixes the issue where buttons appear on the opposite side of the wall
                const buttonDef = {
                    id: object.name,
                    position: [object.position.x, object.position.y, object.position.z],
                    rotation: [object.rotation.x, object.rotation.y + Math.PI, object.rotation.z],
                    wallId: wallId
                };
                
                console.log(`Adjusted canvas button rotation for ${object.name}: Original:`, 
                    [object.rotation.x, object.rotation.y, object.rotation.z], 
                    'Adjusted:', [object.rotation.x, object.rotation.y + Math.PI, object.rotation.z]);
                
                extractedButtons.push(buttonDef);
                
                // Make the button object invisible in the rendered scene
                object.visible = false;
            }
            
            // Handle collision mesh if present
            if (object.name === 'collision_mesh') {
                // Make the collision mesh invisible but keep it for collision detection
                object.visible = false;
                
                // If using a physics system, you would set up collision here
            }
        });
        
        // Update canvas buttons state
        setCanvasButtons(extractedButtons);
        
        // Notify parent component about detected canvas buttons
        if (onCanvasButtonsDetected) {
            onCanvasButtonsDetected(extractedButtons);
        }
        
        // Apply the cloned scene to our group
        if (groupRef.current) {
            // Clear any existing children
            while (groupRef.current.children.length > 0) {
                groupRef.current.remove(groupRef.current.children[0]);
            }
            
            // Add the cloned scene
            groupRef.current.add(clonedScene);
        }
        
        setIsLoaded(true);
        
        // Notify parent that loading is complete
        if (onLoad) {
            onLoad();
        }
        
        // Cleanup function
        return () => {
            // Dispose of geometries and materials to prevent memory leaks
            clonedScene.traverse((object) => {
                if (object.geometry) {
                    object.geometry.dispose();
                }
                
                if (object.material) {
                    if (Array.isArray(object.material)) {
                        object.material.forEach(material => material.dispose());
                    } else {
                        object.material.dispose();
                    }
                }
            });
        };
    }, [scene, nodes, glbPath, onCanvasButtonsDetected, onLoad]);
    
    // Optional: Add debug visualization for canvas buttons
    useFrame(({ camera }) => {
        if (!groupRef.current || !isLoaded) return;
        
        // You could add debug visualization here if needed
    });
    
    return (
        <group ref={groupRef} scale={[scale, scale, scale]}>
            {/* The GLB scene will be added to this group in the useEffect */}
        </group>
    );
};

// Pre-load the GLB to improve performance
useGLTF.preload('/rooms/RoomTemplate.glb');

export default GlbRoom;
